import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { ILicenceDetails } from './account.service';
import { AuthorizationService } from './authorization.service';
import { FeatureToggleService } from './feature-toggle.service';
import { hd, IEmailRecipient, IUserEmailRecipient } from './helpdesk.typings';
import { LicenceInfoService, LicenceStatus } from './license-info.service';
import { Activity, EmailRecipientWithUser } from '../../apps/xsight/src/app/modules/ticket/ticket.model';
import { ResourcePermissionService } from './resource-permission.service';
import { IReporterModel } from './reporter.service';

export interface ITicketSessionChangedStatus {
  ticket: hd.ITicketModel;
  status: TicketSessionStatus;
  associatedDevices: hd.IAssociatedDeviceModel[];
}

export enum TicketSessionStatus {
  Ended,
  Started,
}

export interface ITicketContextService {
  /** The currently selected ticket */
  ticket: hd.ITicketModel;
  /** List of associated tickets */
  activities: hd.IActivityModel[];
  /** List of associated devices */
  associatedDevices: hd.IAssociatedDeviceModel[];
  /** List of devices pending on association */
  devicesToAssociate: hd.IDeviceModel[];
  /** Whether the current license is expired */
  isLicenceExpired: boolean;
  /** Whether the current license is inactive */
  isLicenceInactive: boolean;
  /** List of outbound email recipients for the current ticket */
  outboundEmailRecipientList: IEmailRecipient[] | IUserEmailRecipient[];
  /** Gets license info and sets properties accordingly */
  initializeService: () => void;
  /** Whether the currently logged in user created the currently selected ticket */
  isOwnTicket: () => boolean;
  /** Whether the currently selected ticket is in the draft state */
  isDraftTicket: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to edit the currently selected ticket */
  canEditTicket: () => boolean;

  /** Whether the currently logged in user has sufficient permissions to edit the currently selected ticket's notes */
  canEditNote: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to delete the currently selected ticket's notes */
  canDeleteNote: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to add a new activity to the currently selected ticket */
  canAddActivities: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to delete the currently selected ticket */
  canDeleteTicket: () => boolean;
  /** Upldate License Info */
  updateLicenseInfo: () => hd.ILicenceDetails;

  /** Whether the currently logged in user has sufficient permissions to send outbound email for the current ticket */
  canSendEmail: () => boolean;

  /** Whether the currently logged in user has sufficient permissions to view the current ticket priority */
  canViewPriority: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to edit the current ticket priority */
  canEditPriority: () => boolean;

  canAddDevice: () => boolean;

  /** Whether the currently logged in user has sufficient permissions to view the current ticket tags */
  canViewTag: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to create a user-defined tag */
  canCreateTag: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to associate a tag with the current ticket */
  canAssociateTag: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to remove a tag association from the current ticket */
  canDisassociateTag: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to view SLA of the current ticket */
  canViewSLA: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to associate an Assignee with the current ticket */
  canAddAssignee: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to remove an Assignee from the current ticket */
  canDeleteAssignee: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to associate a Reporter with the current ticket */
  canAddReporter: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to remove a Reporter from the current ticket */
  canDeleteReporter: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to add an attachment to the current ticket:
   * * File,
   * * Screenshot,
   * * Video recording,
   * * Snapshot.
   */
  canAddAttachment: () => boolean;

  /** whether the currently logged in user has sufficient permissions to add a note to the current ticket */
  canAddNote: () => boolean;

  /** whether the currently logged in user has sufficient permissions to add a private note to the current ticket */
  canAddPrivateNote: () => boolean;

  /** whether the currently logged in user has sufficient permissions to add a private note to the current ticket */
  canViewPrivateNote: () => boolean;

  /** Whether the currently logged in user has sufficient permissions to change Status of the current ticket from Draft to Open */
  canSubmitAsOpen: () => boolean;
  /** Whether the currently logged in user has sufficient permissions to edit Status of the current ticket */
  canEditStatus: () => boolean;
  /** whether the currently logged in user has sufficient permissions to add a resolution note to the current ticket */
  canAddResolutionNote: () => boolean;

  allowableActions: string[];
}

@Injectable({ providedIn: 'root' })
export class TicketContextService implements OnDestroy {
  private static readonly AbacAllowableActions = 'AbacAllowableActions';

  // temporarily use Group toggle so that ticket page is not broken.
  private static readonly AbacAllowableActionsGroups = 'AbacAllowableActionsGroups';
  public currentOnGoingRcActivity: hd.IRemoteControlActivity = null;
  private _ticket: hd.ITicketModel = null;
  private _activities: Activity[] = [];
  private _associatedDevices: hd.IAssociatedDeviceModel[] = [];
  private _devicesToAssociate: hd.IDeviceModel[] = [];
  private _islicenceExpiredOrInactive = false;
  private _islicenceInactive = false;
  private _outboundEmailRecipientList: IEmailRecipient[] | IUserEmailRecipient[] = [];
  private licenceInfo: ILicenceDetails;
  public licenceDetails: BehaviorSubject<any> = new BehaviorSubject(null);
  public permissions$: BehaviorSubject<hd.GroupPermissionType[]> = new BehaviorSubject([]);
  public updateManualReporterInfo = new Subject<IReporterModel>();
  // private readonly ticketSessionChanged: BroadcastService<ITicketSessionChangedStatus> = new BroadcastService<ITicketSessionChangedStatus>(BroadcastChannels.onTicketSessionChanged);

  constructor(
    public authorizationService: AuthorizationService,
    private licenceInfoService: LicenceInfoService,
    private featureToggleService: FeatureToggleService,
    private resourcePermissionService: ResourcePermissionService
  ) {}

  public initializeService = (): void => {
    this.updateLicenseInfo();
  };

  public updateLicenseInfo = (): void => {
    this.licenceInfoService
      .getLicenceInformation()
      .pipe(take(1))
      .subscribe(details => {
        this.licenceInfo = details;
        this._islicenceExpiredOrInactive =
          this.licenceInfo.status === LicenceStatus.expired || this.licenceInfo.status === LicenceStatus.inactive;
        this._islicenceInactive = this.licenceInfo.status === LicenceStatus.inactive;
        this.licenceDetails.next(this.licenceInfo);
      });
  };

  public get ticket(): hd.ITicketModel {
    return this._ticket;
  }

  public set ticket(ticket: hd.ITicketModel) {
    if (this._ticket !== ticket) {
      this.resourcePermissionService.removeGroupPermissionObservable(this._ticket?.groupId);
      this._ticket = ticket;
      this.onTicketChanged(ticket);
    }
  }

  public set associatedDevices(associatedDevices: hd.IAssociatedDeviceModel[]) {
    this._associatedDevices = associatedDevices;
  }

  public get associatedDevices(): hd.IAssociatedDeviceModel[] {
    return this._associatedDevices;
  }

  public get devicesToAssociate(): hd.IDeviceModel[] {
    return this._devicesToAssociate;
  }

  public set devicesToAssociate(devicesToAssociate: hd.IDeviceModel[]) {
    this._devicesToAssociate = devicesToAssociate;
  }

  public get activities(): Activity[] {
    return this._activities;
  }

  public set activities(activities: Activity[]) {
    this._activities = activities;
    if (activities && 'allowableActions' in activities) {
      // this.allowableActions = this.allowableActions.concat(activities['allowableActions']);
    }
  }

  public get isLicenceExpired(): boolean {
    return this._islicenceExpiredOrInactive;
  }

  public get isLicenceInactive(): boolean {
    return this._islicenceInactive;
  }

  public set isLicenceInactive(isLicenceInactive: boolean) {
    this._islicenceInactive = isLicenceInactive;
  }

  public get outboundEmailRecipientList(): IEmailRecipient[] | IUserEmailRecipient[] {
    return this._outboundEmailRecipientList;
  }

  public set outboundEmailRecipientList(list: IEmailRecipient[] | IUserEmailRecipient[]) {
    this._outboundEmailRecipientList = list;
  }

  public manualReporterUpdated(updatedReporter: IReporterModel) {
    const reporterIndex = this._outboundEmailRecipientList.findIndex(
      r => r['userId'].toLowerCase() === updatedReporter.reporterId.toLowerCase()
    );
    const fullName = updatedReporter.firstName + ' ' + updatedReporter.lastName;

    if (reporterIndex != -1) {
      var reporter = this._outboundEmailRecipientList[reporterIndex] as unknown as EmailRecipientWithUser;
      if(reporter.userDetail) {
      reporter.userDetail.userName = fullName;
      reporter.userDetail.email = updatedReporter.email;
      reporter.address = updatedReporter.email;
      }

      if(reporter['user']){
        reporter['user'].firstName = updatedReporter.firstName;
        reporter['user'].lastName = updatedReporter.lastName;
        reporter['user'].userName = fullName;
        reporter['user'].email = updatedReporter.email;
        reporter.address = updatedReporter.email;
      }
    }

    this.updateManualReporterInfo.next(updatedReporter);
  }

  public isOwnTicket = (): boolean => {
    let result = false;
    if (this._ticket) {
      if (!this.featureToggleService.isEnabled('AbacAllowableActionsGroups')) {
        if (
          this._ticket.creationUserPublicId &&
          this._ticket.creationUserPublicId === this.authorizationService.getUserId()
        ) {
          result = true;
        }
      } else {
        if (
          (this._ticket.creationUserPublicId &&
            this._ticket.creationUserPublicId === this.authorizationService.getUserId()) ||
          (this._ticket.reporterPublicId && this._ticket.reporterPublicId === this.authorizationService.getUserId())
        ) {
          result = true;
        }
      }
    }
    return result;
  };

  public isDraftTicket = (): boolean => {
    return !!this._ticket && this._ticket.status === 'draft';
  };

  public canEditTicket = (): boolean => {
    if (!this.ticket) {
      return false;
    }

    let result = false;
    if (this.featureToggleService.isEnabled('AbacAllowableActionsGroups')) {
      result = this.allowableActions.includes('EditTicket') && !this._islicenceExpiredOrInactive;
    } else {
      let canEditOwnTicket = this.isOwnTicket();
      canEditOwnTicket = canEditOwnTicket && this.isDraftTicket();
      const isAdminOrTechnician = this.authorizationService.isAdminOrTechnician(this.ticket.groupId);
      result = (isAdminOrTechnician || canEditOwnTicket) && !this._islicenceExpiredOrInactive;
    }
    return result;
  };

  public canCreateIncidents = (): boolean => {
    if (!this.ticket) {
      return false;
    }

    let result = false;
    if (this.featureToggleService.isEnabled('AbacAllowableActionsGroups')) {
      result = this.allowableActions.includes('CreateIncident') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canEditNote = (): boolean => {
    let result = false;
    if (!this._islicenceExpiredOrInactive) {
      result = this.allowableActions.includes('EditNote') && !this._islicenceExpiredOrInactive;
    }
    return result;
  };

  public canDeleteNote = (): boolean => {
    let result = false;
    if (!this._islicenceExpiredOrInactive) {
      result = this.allowableActions.includes('DeleteNote') && !this._islicenceExpiredOrInactive;
    }
    return result;
  };

  public canAddActivities = (): boolean => {
    if (this.ticket) {
      if (this.ticket.status === 'open' && !this.ticket.groupId) {
        return this.isOwnTicket() && !this._islicenceExpiredOrInactive;
      } else {
        return (
          (this.authorizationService.isAdminOrTechnician(this.ticket.groupId) || this.isOwnTicket()) &&
          !this._islicenceExpiredOrInactive
        );
      }
    }
  };

  public canDeleteTicket = (): boolean => {
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      return this.allowableActions.includes('DeleteTicket') && !this._islicenceExpiredOrInactive;
    }

    const groupId = this.ticket ? this.ticket.groupId : null;
    const isAdministrator =
      this.authorizationService.isSystemAdmin() || this.authorizationService.isGroupAdmin(groupId);

    const isOwnerOfDraft =
      this.ticket.status === 'draft' && this.ticket.creationUserPublicId === this.authorizationService.getUserId();

    return (isAdministrator || isOwnerOfDraft) && !this._islicenceExpiredOrInactive;
  };

  public get allowableActions() {
    if (this.ticket && this.ticket.allowableActions) {
      return this.ticket.allowableActions;
    }
    // TODO: Figure out how to NOT allow everything?
    return [
      'AddAttachment',
      'AssociateTag',
      'CreateEmail',
      'CreatePrivateNote',
      'CreatePublicNote',
      'CreateViewEditResolutionNote',
      'DeleteNote',
      'DeleteReporter',
      'DeleteTicket',
      'EditAssignee',
      'EditNote',
      'EditReporter',
      'EditStatus',
      'EditTicket',
      'EditPriority',
      'ViewPriority',
      'ViewSLA',
      'AddDevice',
    ];
  }

  public set allowableActions(allowableActions: string[]) {
    if (this.ticket) {
      this.ticket.allowableActions = allowableActions;
    }
  }

  public canSendEmail = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('CreateEmail') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canViewPriority = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('ViewPriority');
    }
    return result;
  };

  public canEditPriority = (): boolean => {
    let result = false;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('EditPriority') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAddDevice = (): boolean => {
    let result = false;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('AddDevice') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canViewTag = (): boolean => {
    let result = this.allowableActions.includes('ViewTag');
    return result;
  };

  public canCreateTag = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('AssociateTag') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAssociateTag = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('AssociateTag') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canDisassociateTag = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('AssociateTag') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canViewSLA = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('ViewSLA');
    }
    return result;
  };

  public canAddAssignee = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('EditAssignee') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canDeleteAssignee = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('EditAssignee') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAddReporter = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('EditReporter') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAddNote = (): boolean => {
    let result = true;

    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActions)) {
      result =
        (this.allowableActions.includes('CreatePublicNote') || this.allowableActions.includes('CreatePrivateNote')) &&
        !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAddPublicNote = (): boolean => {
    let result = true;

    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActions)) {
      result = this.allowableActions.includes('CreatePublicNote') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAddResolutionNote = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActions)) {
      result = this.allowableActions.includes('CreateViewEditResolutionNote') && !this._islicenceExpiredOrInactive;
    }
    return result;
  };

  public canAddPrivateNote = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActions)) {
      result = this.allowableActions.includes('CreatePrivateNote') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canViewPrivateNote = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActions)) {
      result = this.allowableActions.includes('ViewPrivateNote') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canDeleteReporter = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('DeleteReporter') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canAddAttachment = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('AddAttachment') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  public canSubmitAsOpen = (): boolean => {
    return this.isDraftTicket();
  };

  public canEditStatus = (): boolean => {
    let result = true;
    if (this.featureToggleService.isEnabled(TicketContextService.AbacAllowableActionsGroups)) {
      result = this.allowableActions.includes('EditStatus') && !this._islicenceExpiredOrInactive;
    } else {
      result = this.canEditTicket();
    }
    return result;
  };

  /** Called when a new ticket is set */
  private onTicketChanged = (ticket: hd.ITicketModel) => {
    // Clear ticket-related fields
    const groupId = ticket ? ticket.groupId : null;
    this._outboundEmailRecipientList = [];
    if (groupId) {
      this.resourcePermissionService.getGroupPermissions$(groupId).subscribe(this.permissions$);
    }

    // this.ticketSessionChanged.postMessage({
    //     ticket: this._ticket,
    //     status: TicketSessionStatus.Started,
    //     associatedDevices: this._associatedDevices
    // } as ITicketSessionChangedStatus);
  };

  private hasPermission = (permission: hd.GroupPermissionType): boolean => {
    return this.permissions$.value.includes(permission);
  };

  ngOnDestroy() {
    // if (this.ticketSessionChanged) {
    //     this.ticketSessionChanged.close();
    // }
  }
}
